import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Mail, Send, Loader2, CheckCircle2, Linkedin } from "lucide-react";

import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  "Particuliere schoonmaak",
  "Kantoorschoonmaak",
  "Glazenwassen (vitreuses)",
  "Opleveringsschoonmaak / na-bouw",
  "Diepte­reiniging",
  "Anders",
];

export const ContactQuote = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Vul alle verplichte velden in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Bedankt! We nemen zo snel mogelijk contact met u op.");
      setForm({ name: "", email: "", phone: "", service_type: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Er ging iets mis. Probeer het opnieuw of bel ons direct."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-[#0F172A] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-[#0284C7]/20 blur-3xl" />
        <div className="absolute bottom-0 -right-24 h-[420px] w-[420px] rounded-full bg-[#16A34A]/15 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#16A34A]">
            Contact & Offerte
          </span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-balance">
            Klaar voor een vlekkeloze samenwerking?
          </h2>
          <p className="mt-6 font-body text-slate-300 text-lg leading-relaxed">
            Vul het formulier in en u ontvangt binnen 24u een vrijblijvende
            offerte op maat. Liever bellen? Wij zijn 24/7 bereikbaar.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex items-start gap-4" data-testid="contact-address">
              <span className="mt-0.5 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                <MapPin size={20} className="text-[#16A34A]" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Adres</p>
                <p className="font-display font-bold text-lg">Brederodestraat 94</p>
                <p className="text-slate-300">2018 Antwerpen, België</p>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-phone">
              <span className="mt-0.5 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                <Phone size={20} className="text-[#16A34A]" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Telefoon</p>
                <a href="tel:0466324304" className="font-display font-bold text-lg hover:text-[#0284C7]">
                  0466 32 43 04
                </a>
                <p className="text-slate-300">Direct contact, 24/7</p>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-email">
              <span className="mt-0.5 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                <Mail size={20} className="text-[#16A34A]" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">E-mail</p>
                <a
                  href="mailto:info@dilagroupservices.be"
                  className="font-display font-bold text-lg hover:text-[#0284C7] break-all"
                >
                  info@dilagroupservices.be
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-hours">
              <span className="mt-0.5 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                <Clock size={20} className="text-[#16A34A]" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Openingsuren</p>
                <p className="font-display font-bold text-lg">Open 24 uur</p>
                <p className="text-slate-300">7 dagen op 7</p>
              </div>
            </li>
          </ul>

          <a
            href="https://www.linkedin.com/in/funda-mehmed-bb05653b6"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-linkedin-link"
            className="mt-10 inline-flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 hover:bg-white/10 hover:border-[#0A66C2]/60 transition-all"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A66C2] text-white shrink-0">
              <Linkedin size={20} strokeWidth={2.5} />
            </span>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Verbind op LinkedIn</p>
              <p className="font-display font-bold text-base text-white">Funda Mehmed — Dila Group BV</p>
            </div>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="rounded-3xl bg-white text-[#0F172A] p-7 sm:p-10 shadow-2xl">
            {done ? (
              <div className="text-center py-12" data-testid="quote-success">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#F0FDF4]">
                  <CheckCircle2 size={32} className="text-[#16A34A]" />
                </div>
                <h3 className="mt-6 font-display text-3xl font-bold">Bedankt voor uw aanvraag!</h3>
                <p className="mt-3 text-slate-600 max-w-md mx-auto">
                  We hebben uw bericht goed ontvangen en nemen binnen 24 uur
                  contact met u op.
                </p>
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  data-testid="quote-new-btn"
                  className="mt-8 inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold hover:border-[#0284C7] hover:text-[#0284C7]"
                >
                  Nieuw bericht versturen
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                <h3 className="font-display text-2xl sm:text-3xl font-bold">
                  Vraag uw gratis offerte aan
                </h3>
                <p className="text-sm text-slate-500 -mt-3">
                  Vul onderstaande gegevens in. We reageren binnen 24u.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="q-name" className="font-body">Naam *</Label>
                    <Input
                      id="q-name"
                      data-testid="quote-input-name"
                      placeholder="Uw volledige naam"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="mt-1.5 h-11"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="q-phone" className="font-body">Telefoon *</Label>
                    <Input
                      id="q-phone"
                      data-testid="quote-input-phone"
                      placeholder="0466 12 34 56"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className="mt-1.5 h-11"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="q-email" className="font-body">E-mailadres *</Label>
                  <Input
                    id="q-email"
                    type="email"
                    data-testid="quote-input-email"
                    placeholder="u@voorbeeld.be"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="mt-1.5 h-11"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="q-service" className="font-body">Type dienst *</Label>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => update("service_type", v)}
                  >
                    <SelectTrigger
                      id="q-service"
                      data-testid="quote-input-service"
                      className="mt-1.5 h-11"
                    >
                      <SelectValue placeholder="Kies een dienst" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s.replace(/\s/g, "-")}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="q-message" className="font-body">Uw bericht</Label>
                  <Textarea
                    id="q-message"
                    data-testid="quote-input-message"
                    placeholder="Vertel ons kort over uw situatie, oppervlakte, frequentie..."
                    rows={4}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="mt-1.5 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0284C7] hover:bg-[#0369A1] disabled:opacity-60 text-white font-semibold px-7 py-4 shadow-lg shadow-sky-200 transition-all hover:-translate-y-0.5"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Versturen...
                    </>
                  ) : (
                    <>
                      Verstuur Aanvraag
                      <Send size={16} className="transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
                <p className="text-xs text-slate-500 text-center">
                  Door te verzenden gaat u akkoord met de verwerking van uw
                  gegevens voor het opmaken van uw offerte.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
